import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import type {
    AnalyticsCumulatedSalesItem,
    ChannelConsolidatedSales,
    ChannelConsolidatedSalesStoreItem,
} from "helpers/parsers/parseConsolidatedSales";
import {
    parseConsolidatedSales,
    parseConsolidatedSalesTotal,
    parseTopPerformingTitle,
} from "helpers/parsers/parseConsolidatedSales";
import { formatDateToIso, mergeDeep, translationsWithFallback } from "helpers/utils";
import { toast } from "react-toastify";
import type { AnalyticsFilters, IDatesRange } from "types";

import type { ChannelKind, TFetchStatus } from "./types";

const sanitizeDate = (date) => {
    const parsedDate = new Date(date);

    if (isNaN(parsedDate.getTime())) {
        throw new Error("Invalid date");
    }

    return parsedDate.toLocaleDateString("en-CA");
};

export const fetchDashboardData = createAsyncThunk(
    "analytics/fetchDashboardData",
    async ({ dates, type }: { dates: IDatesRange; type?: AnalyticsChartType }, { rejectWithValue }) => {
        const params = { dates };
        try {
            const [topPerformersResponse, periodMetricsResponse] = await Promise.all([
                axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/analytics/top-performers`, {
                    params,
                }),
                axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/analytics/periods/metrics`, {
                    params,
                }),
            ]);

            const topPerformers = topPerformersResponse.data.data as {
                topPerformingTitles: AnalyticsCumulatedSalesItem[];
                topPerformingBeneficiaries: TopPerformingData[];
                mostPerformantChannels: TopPerformingData[];
            };

            const periodMetrics = periodMetricsResponse.data.data as {
                metrics: {
                    nbTitles: { N: number; N_1: number };
                    avgSales: { N: number; N_1: number };
                    avgRoyalties: { N: number; N_1: number };
                    totalSales: { N: number; N_1: number };
                    totalRoyalties: { N: number; N_1: number };
                };
            };

            const topPerformingTitles = topPerformers.topPerformingTitles.map((o) => parseTopPerformingTitle(o));

            return {
                data: {
                    ...topPerformers,
                    topPerformingTitles,
                    ...periodMetrics,
                },
                type,
            };
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

const formatAnalyticsFilters = (filters: AnalyticsFilters) => {
    const { dates, ...rest } = filters;

    const queryObj: Record<string, string | number | (string | number)[] | boolean | Date> = {
        ...rest,
        ...(dates || {}),
    };

    return queryObj;
};

const getSortColumnName = (sortProperty: string) => {
    switch (sortProperty) {
        case "title":
            return "name";
        case "rev":
            return "turnoverPostDistributor";
        case "ca-ppht":
            return "turnover";
        case "qty-returned":
            return "quantitiesReturned";
        case "qty-net":
            return "quantities";

        case "cumulated-rev":
            return "cumulatedTurnoverPostDistributor";
        case "cumulated-ca-ppht":
            return "cumulatedTurnover";
        case "cumulated-qty-returned":
            return "cumulatedQuantitiesReturned";
        case "cumulated-qty-net":
            return "cumulatedQuantities";
        default:
            return sortProperty;
    }
};

type FetchChannelsSalesParams = {
    dates: IDatesRange;
    titleId: number;
    type: AnalyticsChartType;
};

export const fetchAnalyticsChannelsSales = createAsyncThunk(
    "analytics/fetchAnalyticsChannelsSales",
    async ({ titleId, dates, type }: FetchChannelsSalesParams, { rejectWithValue }) => {
        const params = formatAnalyticsFilters({ titleIds: [titleId], dates });
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/analytics/channels/title/sales`,
                {
                    params: {
                        ...params,
                        startDate: params.startDate ? sanitizeDate(params.startDate) : undefined,
                        endDate: params.endDate ? sanitizeDate(params.endDate) : undefined,
                    },
                },
            );
            return { data: response.data.data, type };
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchAnalyticsSales = createAsyncThunk(
    "analytics/fetchAnalyticsSales",
    async ({ params, type }: { params: FetchSalesParams; type: AnalyticsChartType }, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/analytics/sales", { params });
            return { data: response.data.data, type };
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchAnalyticsCountries = createAsyncThunk(
    "analytics/fetchAnalyticsCountries",
    async ({ params }: { params: FetchSalesParams }, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/analytics/sales", { params });
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const refreshSalesAnalytics = createAsyncThunk("analytics/refresh-sales", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/analytics/refresh-sales");
        return response.data.data;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const fetchAnalyticsCachedSales = createAsyncThunk(
    "analytics/cached-sales",
    async (
        {
            storeKey,
            withTotal,
            groupMethods,
            ...filters
        }: {
            storeKey: string;
            withTotal?: boolean;
            groupMethods?: GroupMethod[];
        } & AnalyticsFilters,
        { rejectWithValue },
    ) => {
        try {
            const params = formatAnalyticsFilters(filters);
            const page = filters.page || 1;

            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/analytics/consolidated-sales", {
                params: {
                    ...params,
                    startDate: params.startDate ? sanitizeDate(params.startDate) : undefined,
                    endDate: params.endDate ? sanitizeDate(params.endDate) : undefined,
                    page,
                    withTotal,
                    groupMethods,
                    fromCache: true,
                },
            });

            const results = response.data.data;
            const isDone = !results?.length || !params.limit || results.length < params.limit;

            return {
                [storeKey]: {
                    status: { isDone, page },
                    pages: {
                        [page]: results,
                    },
                },
            };
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const generateAnalyticsConsolidatedSalesReport = createAsyncThunk(
    "analytics/consolidated-sales-report",
    async (filters: AnalyticsFilters, { rejectWithValue }) => {
        try {
            const params = formatAnalyticsFilters(filters);

            if (params.sortColumn) {
                params.sortColumn = getSortColumnName(`${params.sortColumn}`);
                params.channelIds = [];
            }

            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + "/analytics/consolidated-sales/report",
                {
                    params: {
                        ...params,
                        startDate: params.startDate ? sanitizeDate(params.startDate) : undefined,
                        endDate: params.endDate ? sanitizeDate(params.endDate) : undefined,
                    },
                },
            );

            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error?.response?.data);
        }
    },
);

export const fetchAnalyticsConsolidatedSales = createAsyncThunk(
    "analytics/consolidated-sales",
    async (
        {
            withTotal = false,
            fromCache = false,
            storeKey,
            groupMethods,
            ...filters
        }: {
            withTotal?: boolean;
            fromCache?: boolean;
            storeKey: string;
            groupMethods?: GroupMethod[];
        } & AnalyticsFilters,
        { rejectWithValue },
    ) => {
        try {
            const page = filters.page || 1;
            const channelId = filters.channelIds?.[0] || "all";
            const _filters: AnalyticsFilters = { ...filters, page, limit: 20, channelIds: [channelId] };

            const params = formatAnalyticsFilters(_filters);

            if (params.sortColumn) {
                params.sortColumn = getSortColumnName(`${params.sortColumn}`);
            }

            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/analytics/consolidated-sales", {
                params: {
                    ...params,
                    startDate: params.startDate ? sanitizeDate(params.startDate) : undefined,
                    endDate: params.endDate ? sanitizeDate(params.endDate) : undefined,
                    groupMethods,
                    fromCache,
                    withTotal,
                },
            });

            const results = response.data.data;
            const isDone = !results?.length || !params.limit || results.length < params.limit;

            const rowOfTotal = withTotal && results[0] ? parseConsolidatedSalesTotal(results[0]) : undefined;

            const storeItem = {
                status: { page, isDone },
                pages: { [page]: results.map((o) => parseConsolidatedSales(o)) },
                channelId,
            };

            if (rowOfTotal) {
                storeItem["total"] = rowOfTotal;
            }

            return {
                [storeKey]: storeItem,
            };
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchAnalyticsChannelsPeriodSales = createAsyncThunk(
    "analytics/channels-period-sales",
    async (filters: AnalyticsFilters, { rejectWithValue }) => {
        try {
            const params = formatAnalyticsFilters(filters);

            const storeKey = getStoreKeyFromFilters(filters);

            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + "/analytics/channels/periods/sales",
                {
                    params: {
                        ...params,
                        startDate: params.startDate ? sanitizeDate(params.startDate) : undefined,
                        endDate: params.endDate ? sanitizeDate(params.endDate) : undefined,
                    },
                },
            );

            const results = response.data.data;

            return {
                [storeKey]: results,
            };
        } catch (error) {
            /* empty */
        }
    },
);

export type GroupMethod =
    | "perYear"
    | "perMonth"
    | "perDate"
    | "perChannel"
    | "perTitle"
    | "perCountry"
    | "dontGroup"
    | "groupAll";

export type GroupedChannelConsolidatedSales = ChannelConsolidatedSales & {
    total_turnover?: number;
    total_channel_kind_turnover?: number;
    total_channel_id_turnover?: number;
    total_turnover_1?: number;
    total_channel_kind_turnover_1?: number;
    total_channel_id_turnover_1?: number;
    total_turnover_2?: number;
    total_channel_kind_turnover_2?: number;
    total_channel_id_turnover_2?: number;
    total_quantities?: number;
    total_channel_kind_quantities?: number;
    total_channel_id_quantities?: number;
};

export type ChannelPeriodSalesFromAPI = {
    salesN: GroupedChannelConsolidatedSales[];
    salesN_1: GroupedChannelConsolidatedSales[];
};

export type AnalyticsChartType =
    | "line-chart"
    | "pie-chart-per-channel"
    | "pie-chart-per-country"
    | "bar-chart"
    | "indicators"
    | "top-performing-data"
    | "channels-metrics"
    | "consolidated-sales";

export type FetchSalesParams = {
    startDate: Date;
    endDate: Date;
    groupMethod: GroupMethod;
    titleIds?: number[];
    channelIds?: number[];
    countryCodes?: number[];
};

export type TopPerformingData = {
    id: number;
    titleId?: number;
    type?: number;
    kind?: number;
    name: string;
    amount: number | string;
    avatarUrl?: string;
    author?: string;
};

export interface DashboardData {
    topPerformingTitles: TopPerformingData[];
    topPerformingBeneficiaries: TopPerformingData[];
    mostPerformantChannels: TopPerformingData[];
    metrics: {
        nbTitles: { N: number; N_1: number };
        avgSales: { N: number; N_1: number };
        avgRoyalties: { N: number; N_1: number };
        totalSales: { N: number; N_1: number };
        totalRoyalties: { N: number; N_1: number };
    };
}

type ChannelSalesPeriodValues = { N: number | null; N_1: number | null; growthIndex: number | null };

export type ChannelSalesData = {
    quantities: ChannelSalesPeriodValues;
    turnover: ChannelSalesPeriodValues;
    turnover_1: ChannelSalesPeriodValues;
    turnover_2: ChannelSalesPeriodValues;
};

export type ChannelConsolidatedSalesData = {
    total_channel_id_quantities: number;
    total_channel_id_turnover: number;
    total_channel_id_turnover_1?: number;
    total_channel_id_turnover_2?: number;
};

export type ChannelSales = {
    id: number;
    kind: ChannelKind;
    name: string;
    data: ChannelSalesData | ChannelConsolidatedSalesData;
};

export type TConsolidatedChannelSales = {
    titleId: number;
    avatarUrl: string;
    name: string;
    ean13: string;
    date: string;
    saleYear: number;
    saleMonth: number;
    channelId: number;
    channelName: string;
    channelKind: number;
    periodId: number;
    countryMarket: string;
    optimalPrice: number;
    quantities: number;
    quantitiesReturned: number;
    turnover: number;
    turnoverPostDistributor: number;
    companyId: number;
    saleId: number;
    optimalPriceCurrency: string;
    distributorPercentage: null;
    total_turnover: number;
    total_channel_kind_turnover: number;
    total_channel_id_turnover: number;
    total_quantities: number;
    total_channel_kind_quantities: number;
    total_channel_id_quantities: number;
};

export interface TitleSales {
    id: number;
    channelId: number;
    channelName: string;
    countryMarket: string;
    date: string;
    ean13: string;
    name: string;
    optimalPrice: number;
    quantities: number;
    titleId: number;
    turnover: number;
    turnover_1?: number | null;
    turnover_2?: number | null;
}

interface AnalyticsDashboard {
    indicators: DashboardData | undefined;
    "top-performing-data": DashboardData | undefined;
}

export interface AnalyticsChannelsSales {
    "channels-metrics": { data: ChannelSales[] | TConsolidatedChannelSales[]; titleIds: string[] };
}

export interface AnalyticsSales {
    "line-chart": TitleSales[];
    "pie-chart-per-channel": TitleSales[];
    "pie-chart-per-country": TitleSales[];
    "bar-chart": TitleSales[];
}

export type AnalyticsSalesStatus = Record<string, TFetchStatus>;

export type AnalyticsConsolidatedSalesStatus = Record<string, TFetchStatus>;

interface AnalyticsState {
    dashboard: DashboardData | undefined;
    dashboardStatus: AnalyticsSalesStatus;
    analyticsSales: AnalyticsSales;
    analyticsCountries: string[];
    analyticsDashboard: AnalyticsDashboard;
    analyticsChannelsSales: AnalyticsChannelsSales;

    analyticsSalesRefresh: Date | undefined;

    analyticsConsolidatedSales: Record<
        string,
        {
            status: { isDone: boolean; page: number };
            pages: Record<string, ChannelConsolidatedSalesStoreItem[]>;
            channelId: string;
            total?: ChannelConsolidatedSalesStoreItem;
        }
    >;

    analyticsCachedSales: Record<
        string,
        {
            status: { isDone: boolean; page: number };
            pages: Record<string, AnalyticsCumulatedSalesItem[]>;
        }
    >;
    analyticsCachedSalesStatus: Record<string, TFetchStatus>;

    analyticsChannelsPeriodSales: Record<string, ChannelPeriodSalesFromAPI>;
    analyticsChannelsPeriodSalesStatus: TFetchStatus;

    channelsSalesStatus: TFetchStatus;
    analyticsSalesStatus: AnalyticsSalesStatus;

    /**
     * GET analytics table status
     */
    refreshAnalyticsStatus: TFetchStatus;
    analyticsCountriesStatus: TFetchStatus;

    analyticsSalesRefreshStatus: TFetchStatus;
    analyticsConsolidatedSalesStatus: AnalyticsConsolidatedSalesStatus;

    analyticsConsolidatedSalesReport: any;
    analyticsConsolidatedSalesReportStatus: TFetchStatus;
}

const initialAnalyticsSales = {
    "line-chart": [],
    "pie-chart-per-channel": [],
    "pie-chart-per-country": [],
    "bar-chart": [],
};

const initialAnalyticsSalesStatus: AnalyticsSalesStatus = {
    "line-chart": "idle",
    "pie-chart-per-channel": "idle",
    "pie-chart-per-country": "idle",
    "bar-chart": "idle",
};

const initialState: AnalyticsState = {
    dashboard: undefined,
    dashboardStatus: { indicators: "idle", "top-performing-data": "idle" },
    analyticsSales: initialAnalyticsSales,
    analyticsCountries: [],
    analyticsDashboard: {
        indicators: undefined,
        "top-performing-data": undefined,
    },
    analyticsChannelsSales: { "channels-metrics": { data: [], titleIds: [] } }, // If there is only channelsMetrics here, do we need this? Couldn't we simply pass the data wihthout the type?
    analyticsConsolidatedSales: {},

    analyticsChannelsPeriodSales: {},
    analyticsChannelsPeriodSalesStatus: "idle",

    channelsSalesStatus: "idle",
    analyticsSalesStatus: initialAnalyticsSalesStatus,
    refreshAnalyticsStatus: "idle",
    analyticsCountriesStatus: "idle",
    analyticsConsolidatedSalesStatus: {},

    analyticsSalesRefresh: undefined,
    analyticsSalesRefreshStatus: "idle",

    analyticsCachedSales: {},
    analyticsCachedSalesStatus: {},

    analyticsConsolidatedSalesReport: {},
    analyticsConsolidatedSalesReportStatus: "idle",
};

// Function to generate a unique key for the store based on filters
// This is used to cache the results of the API call
export const getStoreKeyFromFilters = (
    filters: AnalyticsFilters,
    groupMethods?: GroupMethod[],
    fromCache?: boolean,
) => {
    const startDateStr = filters.dates?.startDate ? formatDateToIso(filters.dates.startDate) : null;
    const endDateStr = filters.dates?.endDate ? formatDateToIso(filters.dates.endDate) : null;

    return [
        ...(filters.channelIds || []).map((o) => `ch-${o}`),
        ...(filters.periodIds || []).map((o) => `p-${o}`),
        ...(filters.titleIds || []).map((o) => `t-${o}`),
        ...(groupMethods || []).map((o) => `gr-${o}`),
        ...(filters.countryMarkets || []).map((o) => `co-${o}`),
        startDateStr || "",
        endDateStr || "",
        filters.sortColumn || "",
        filters.sortDirection || "",
        `fromCache-${!!fromCache}`,
    ]
        .sort() // Sort to ensure the same filters always generate the same key
        .join("");
};

export const analyticsSlice = createSlice({
    name: "analytics",
    initialState,
    reducers: {
        resetAllGraphData(state) {
            // Reset fetch status
            state.channelsSalesStatus = "idle";
            state.analyticsSalesStatus = initialAnalyticsSalesStatus;
            state.dashboardStatus = { indicators: "idle", "top-performing-data": "idle" };
            state.analyticsConsolidatedSalesStatus = {};

            // Reset data
            state.analyticsSales = initialAnalyticsSales;
            state.analyticsChannelsSales = { "channels-metrics": { data: [], titleIds: [] } };
            state.analyticsDashboard = {
                indicators: undefined,
                "top-performing-data": undefined,
            };
            state.analyticsConsolidatedSales = {};

            state.analyticsSalesRefresh = undefined;
            state.analyticsSalesRefreshStatus = "idle";
        },

        setRefreshAnalyticsStatus(state, action) {
            state.refreshAnalyticsStatus = action.payload;
        },

        resetAnalyticsConsolidatedSales(state) {
            state.analyticsConsolidatedSales = {};
            state.analyticsConsolidatedSalesStatus = {};
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchDashboardData.fulfilled, (state, action) => {
            const { data, type } = action.payload;
            state.dashboard = data;
            if (type) {
                state.analyticsDashboard = { ...state.analyticsDashboard, [type]: data };
                state.dashboardStatus = { ...state.dashboardStatus, [type]: "succeeded" };
            }
        });
        builder.addCase(fetchDashboardData.rejected, (state, { payload, meta }) => {
            const { type } = meta.arg;
            if (type) {
                state.dashboardStatus = { ...state.dashboardStatus, [type]: "failed" };
            }
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchDashboardData.pending, (state, action) => {
            const { type } = action.meta.arg;
            if (type) {
                state.dashboardStatus = { ...state.dashboardStatus, [type]: "pending" };
            }
        });
        builder.addCase(fetchAnalyticsChannelsSales.fulfilled, (state, action) => {
            state.channelsSalesStatus = "succeeded";
            const { data, type } = action.payload;
            state.analyticsChannelsSales = {
                ...state.analyticsChannelsSales,
                [type]: { data: data.salesN, titleIds: data.salesN.map((sale) => sale.titleId) },
            };
        });
        builder.addCase(fetchAnalyticsChannelsSales.pending, (state) => {
            state.channelsSalesStatus = "pending";
        });
        builder.addCase(fetchAnalyticsChannelsSales.rejected, (state, { payload }) => {
            state.channelsSalesStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchAnalyticsSales.pending, (state, action) => {
            const { type } = action.meta.arg;
            state.analyticsSalesStatus = { ...state.analyticsSalesStatus, [type]: "pending" };
        });
        builder.addCase(fetchAnalyticsSales.fulfilled, (state, action) => {
            const { data: titleSalesData, type } = action.payload;
            state.analyticsSales = { ...state.analyticsSales, [type]: titleSalesData };
            state.analyticsSalesStatus = { ...state.analyticsSalesStatus, [type]: "succeeded" };
        });
        builder.addCase(fetchAnalyticsSales.rejected, (state, { payload, meta }) => {
            const { type } = meta.arg;
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.analyticsSalesStatus = { ...state.analyticsSalesStatus, [type]: "failed" };
        });

        builder.addCase(fetchAnalyticsCountries.pending, (state, action) => {
            state.analyticsCountriesStatus = "pending";
        });
        builder.addCase(fetchAnalyticsCountries.fulfilled, (state, action) => {
            const titleSalesData = action.payload;
            state.analyticsCountries = [...new Set(titleSalesData.map((item) => item.countryMarket))] as string[];
            state.analyticsCountriesStatus = "succeeded";
        });
        builder.addCase(fetchAnalyticsCountries.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.analyticsCountriesStatus = "rejected";
        });

        builder.addCase(refreshSalesAnalytics.pending, (state, action) => {
            state.analyticsSalesRefreshStatus = "pending";
        });
        builder.addCase(refreshSalesAnalytics.fulfilled, (state, action) => {
            const data = action.payload;
            state.analyticsSalesRefresh = new Date();
            state.analyticsSalesRefreshStatus = "succeeded";
        });
        builder.addCase(refreshSalesAnalytics.rejected, (state, { payload, meta }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.analyticsSalesRefreshStatus = "rejected";
        });

        builder.addCase(fetchAnalyticsConsolidatedSales.pending, (state, action) => {
            state.analyticsConsolidatedSalesStatus = mergeDeep(state.analyticsConsolidatedSalesStatus, {
                [action.meta.arg.storeKey]: "pending",
            });
        });
        builder.addCase(fetchAnalyticsConsolidatedSales.fulfilled, (state, action) => {
            const data = action.payload;
            state.analyticsConsolidatedSales = mergeDeep(state.analyticsConsolidatedSales, data);
            state.analyticsConsolidatedSalesStatus = mergeDeep(state.analyticsConsolidatedSalesStatus, {
                [action.meta.arg.storeKey]: "succeeded",
            });
        });
        builder.addCase(fetchAnalyticsConsolidatedSales.rejected, (state, { payload, meta }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.analyticsConsolidatedSalesStatus = mergeDeep(state.analyticsConsolidatedSalesStatus, {
                [meta.arg.storeKey]: "failed",
            });
        });

        builder.addCase(generateAnalyticsConsolidatedSalesReport.pending, (state, action) => {
            state.analyticsConsolidatedSalesReportStatus = "pending";
        });
        builder.addCase(generateAnalyticsConsolidatedSalesReport.fulfilled, (state, action) => {
            const data = action.payload;
            state.analyticsConsolidatedSalesReport = data;
            state.analyticsConsolidatedSalesReportStatus = "succeeded";
        });
        builder.addCase(generateAnalyticsConsolidatedSalesReport.rejected, (state, { payload, meta }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.analyticsConsolidatedSalesReportStatus = "rejected";
        });

        builder.addCase(fetchAnalyticsCachedSales.pending, (state, action) => {
            state.analyticsCachedSalesStatus = mergeDeep(state.analyticsCachedSalesStatus, {
                [action.meta.arg.storeKey]: "pending",
            });
        });
        builder.addCase(fetchAnalyticsCachedSales.fulfilled, (state, action) => {
            const data = action.payload;
            state.analyticsCachedSales = mergeDeep(state.analyticsCachedSales, data);
            state.analyticsCachedSalesStatus = mergeDeep(state.analyticsCachedSalesStatus, {
                [action.meta.arg.storeKey]: "succeeded",
            });
        });
        builder.addCase(fetchAnalyticsCachedSales.rejected, (state, { payload, meta }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.analyticsCachedSalesStatus = mergeDeep(state.analyticsCachedSalesStatus, {
                [meta.arg.storeKey]: "rejected",
            });
        });

        builder.addCase(fetchAnalyticsChannelsPeriodSales.pending, (state, action) => {
            state.analyticsChannelsPeriodSalesStatus = "pending";
        });
        builder.addCase(fetchAnalyticsChannelsPeriodSales.fulfilled, (state, action) => {
            const data = action.payload;
            state.analyticsChannelsPeriodSales = mergeDeep(state.analyticsChannelsPeriodSales, data);
            state.analyticsChannelsPeriodSalesStatus = "succeeded";
        });
        builder.addCase(fetchAnalyticsChannelsPeriodSales.rejected, (state, { payload, meta }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.analyticsChannelsPeriodSalesStatus = "rejected";
        });
    },
});

export const { resetAllGraphData, setRefreshAnalyticsStatus, resetAnalyticsConsolidatedSales } = analyticsSlice.actions;

export default analyticsSlice.reducer;
